import { useState } from 'react'
import MFAccordion from '../../../../../../../components/ui-components/MFAccordion'
import { LumpsumPlanTransferType } from '../../api/recommendation.types'
import {
  getPlanRecommendationTabFund,
  type PlanRecommendationPairView
} from '../../pair/planRecommendationPair.types'
import { StepsToRebalanceAccordionBody } from './StepsToRebalanceAccordionBody'
import { EventNames } from '../../../../../../../types/misc.types'
import { trackEvent } from '../../../../../../../utils/appBridge'
import { PlansLumpsumSheetEventIds, PlansScreen } from '../../../../analytics/plansEvents'

export enum StepsAccordionPlanType {
  REBALANCE = 'REBALANCE',
  REALLOCATE = 'REALLOCATE'
}

const STEPS_ACCORDION_TITLES: Record<StepsAccordionPlanType, string> = {
  [StepsAccordionPlanType.REBALANCE]: 'Steps to Rebalance',
  [StepsAccordionPlanType.REALLOCATE]: 'Steps to Reallocate'
}

type StepsToRebalanceAccordionProps = {
  stepsPlanType?: StepsAccordionPlanType
  pair: PlanRecommendationPairView
  analyticsScreen?: string
}

function getStepsToRebalanceSubtext(isSwp: boolean): string {
  if (isSwp) {
    return 'Setup SWP and reinvest'
  }
  return 'Withdraw and reinvest'
}

export function StepsToRebalanceAccordion({
  stepsPlanType = StepsAccordionPlanType.REBALANCE,
  pair,
  analyticsScreen = PlansScreen.LumpsumAllocationRecommendation
}: StepsToRebalanceAccordionProps) {
  const [isExpanded, setIsExpanded] = useState(false)
  // transferType is per sell↔buy edge (selected tab), not the sheet header fund.
  const isSwp = getPlanRecommendationTabFund(pair).transferType === LumpsumPlanTransferType.SWP
  const accordionTitle = STEPS_ACCORDION_TITLES[stepsPlanType]

  return (
    <MFAccordion
      title={accordionTitle}
      iconComponent={<StepsToRebalanceAccordionIcon />}
      isExpanded={isExpanded}
      onAccordionClick={() => {
        const tabFund = getPlanRecommendationTabFund(pair)
        const fundName = tabFund?.fundName ?? ''
        const fundIsin = tabFund?.isin ?? ''
        trackEvent({
          eventName: EventNames.UserClicked,
          payload: {
            ScreenName: analyticsScreen,
            eventId: PlansLumpsumSheetEventIds.StepsAccordion,
            buttonLabel: accordionTitle,
            ...(fundName ? { fundName } : {}),
            ...(fundIsin ? { fundIsin } : {})
          }
        })
        setIsExpanded((currentExpanded) => !currentExpanded)
      }}
      isBorderTop
      accordionHeaderClassName="!px-5"
      middleContent={(
        <div className="flex flex-col">
          <div className="text-[14px] font-[500] leading-[130%] text-[#232323]">{accordionTitle}</div>
          <div className="text-[14px] font-[400] leading-[150%] text-[#6A6E6E]">
            {getStepsToRebalanceSubtext(isSwp)}
          </div>
        </div>
      )}
    >
      <StepsToRebalanceAccordionBody pair={pair} isSwp={isSwp} />
    </MFAccordion>
  )
}

export function StepsToRebalanceAccordionIcon() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 40 40" fill="none" aria-hidden>
      <path
        d="M25.8594 28.0525C25.8594 28.2466 25.9365 28.4329 26.0738 28.5702C26.2111 28.7075 26.3973 28.7846 26.5915 28.7846C26.7857 28.7846 26.9719 28.7075 27.1092 28.5702C27.2465 28.4329 27.3237 28.2466 27.3237 28.0525C27.3237 27.8583 27.2465 27.6721 27.1092 27.5348C26.9719 27.3974 26.7857 27.3203 26.5915 27.3203C26.3973 27.3203 26.2111 27.3974 26.0738 27.5348C25.9365 27.6721 25.8594 27.8583 25.8594 28.0525Z"
        stroke="#232323"
        strokeWidth="2"
      />
      <path
        d="M19.2734 28.0525C19.2734 28.2466 19.3506 28.4329 19.4879 28.5702C19.6252 28.7075 19.8114 28.7846 20.0056 28.7846C20.1998 28.7846 20.386 28.7075 20.5233 28.5702C20.6606 28.4329 20.7377 28.2466 20.7377 28.0525C20.7377 27.8583 20.6606 27.6721 20.5233 27.5348C20.386 27.3974 20.1998 27.3203 20.0056 27.3203C19.8114 27.3203 19.6252 27.3974 19.4879 27.5348C19.3506 27.6721 19.2734 27.8583 19.2734 28.0525Z"
        stroke="#232323"
        strokeWidth="2"
      />
      <path
        d="M12.6875 28.0525C12.6875 28.2466 12.7646 28.4329 12.9019 28.5702C13.0392 28.7075 13.2255 28.7846 13.4196 28.7846C13.6138 28.7846 13.8 28.7075 13.9373 28.5702C14.0746 28.4329 14.1518 28.2466 14.1518 28.0525C14.1518 27.8583 14.0746 27.6721 13.9373 27.5348C13.8 27.3974 13.6138 27.3203 13.4196 27.3203C13.2255 27.3203 13.0392 27.3974 12.9019 27.5348C12.7646 27.6721 12.6875 27.8583 12.6875 28.0525Z"
        stroke="#232323"
        strokeWidth="2"
      />
      <path
        d="M13 17.3333L18.3333 22L29 10"
        stroke="#232323"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}
